"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NoiseForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="border-t border-b border-border py-16">
        <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">
          Request processed
        </p>
        <h3 className="font-default text-4xl md:text-5xl font-medium text-foreground leading-[1.1] tracking-tight mb-6">
          {successMessage}
        </h3>
        <Button onClick={resetForm} variant="outline" className="border border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background px-7 py-4 h-auto font-medium">
          Send another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <FormField control={control} name="name" rules={{
      required: "Your name is required",
      minLength: {
        value: 2,
        message: "Must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="group border-b border-border py-5 transition-all duration-300 focus-within:border-primary focus-within:border-b-2">
            <FormLabel className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground font-normal">
              Full name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Smith" {...field} className="border-0 bg-transparent px-0 h-10 text-lg md:text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-0 transition-all duration-300 focus-visible:h-14" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem className="group border-b border-border py-5 transition-all duration-300 focus-within:border-primary focus-within:border-b-2">
            <FormLabel className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground font-normal">
              Email address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@company.com" {...field} className="border-0 bg-transparent px-0 h-10 text-lg md:text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-0 transition-all duration-300 focus-visible:h-14" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem className="group border-b border-border py-5 transition-all duration-300 focus-within:border-primary focus-within:border-b-2">
            <FormLabel className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground font-normal">
              Phone number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="border-0 bg-transparent px-0 h-10 text-lg md:text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-0 transition-all duration-300 focus-visible:h-14" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="group border-b border-border py-5 transition-all duration-300 focus-within:border-primary focus-within:border-b-2">
            <FormLabel className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground font-normal">
              Organization
            </FormLabel>
            <FormControl>
              <Input placeholder="Your Company" {...field} className="border-0 bg-transparent px-0 h-10 text-lg md:text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-0 transition-all duration-300 focus-visible:h-14" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please describe your requirements",
      minLength: {
        value: 10,
        message: "Please provide more detail"
      }
    }} render={({
      field
    }) => <FormItem className="group border-b border-border py-5 transition-all duration-300 focus-within:border-primary focus-within:border-b-2">
            <FormLabel className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground font-normal">
              Project details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Scope, objectives, timeline..." {...field} className="border-0 bg-transparent px-0 min-h-[88px] text-lg md:text-xl text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-0 focus-visible:border-0 transition-all duration-300 focus-visible:min-h-[200px] resize-none" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="border-b border-destructive py-4">
          <p className="font-mono text-xs text-destructive">{submitError}</p>
        </div>}

      <div className="flex justify-end pt-12">
        <Button type="submit" disabled={isSubmitting} className="group bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-4 h-auto font-medium tracking-wide">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Submitting...
            </> : <>
              Submit Inquiry
              <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </>}
        </Button>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="12839" id="direct-inquiry" className="bg-background text-foreground py-32 md:py-44 px-[clamp(1.25rem,4vw,3rem)] light">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-12">
          <div className="lg:col-span-5">
            <div ref={headlineRef} className={`lg:sticky lg:top-[120px] transition-all duration-700 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <h2 className="font-default font-semibold text-foreground leading-[1.0] tracking-[-0.04em] text-[clamp(3.5rem,8vw,6rem)]">
                <span className="block">Initiate</span>
                <span className="block text-primary">collaboration.</span>
              </h2>

              <p className="mt-12 font-default text-lg md:text-xl text-muted-foreground leading-[1.6] max-w-md">
                Connect with ITLS to discuss your strategic requirements. We provide tailored expertise and professional support to ensure your objectives are met with precision.
              </p>

              <div className="mt-8">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3">
                  Direct contact
                </p>
                <Link to="mailto:partners@itls.com" className="inline-block font-default text-lg font-medium text-foreground relative group">
                  partners@itls.com
                  <span className="absolute bottom-0 left-0 h-[1px] bg-primary w-0 group-hover:w-full transition-all duration-300" />
                </Link>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div ref={formRef} className={`transition-all duration-700 ease-out ${formVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <FormProvider formKey="direct_inquiry_form_connect" formId="direct_inquiry_form_connect" sectionName="direct-inquiry" defaultValues={{
              name: "",
              email: "",
              phone: "",
              company: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Submit Inquiry" successMessage="Inquiry received. Our team will contact you shortly." errorMessage="Submission failed. Please verify your details or email us directly.">
                <NoiseForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}